import React from 'react'

import {
  useConversationStore,
  useConversationMessageStore,
} from 'stores/conversation'
import { useAuth } from 'utils/auth'
import ConversationHeader from 'app/ConversationPage/components/ConversationHeader'
import ConversationInputBox from 'app/ConversationPage/components/ConversationInputBox'
import ConversationMessage from 'app/ConversationPage/components/ConversationMessage'
import ConversationMessageError from 'app/ConversationPage/components/ConversationMessageError'
import ConversationMessageLoading from 'app/ConversationPage/components/ConversationMessageLoading'
import ConversationSuggestions from 'app/ConversationPage/components/ConversationSuggestions'
import NewSessionConfirmSheet from 'app/ConversationPage/components/NewSessionConfirmSheet'
import { CONVERSATION_STORAGE_KEY } from 'app/ConversationPage/configs'
import useConversation from 'app/ConversationPage/hooks/useConversation'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'
import { ForbiddenError } from 'app/ConversationPage/utils/customError'

interface IConversationPageAppProps {
  isGuruAIEligible: boolean
}
const ConversationPageApp: React.FC<IConversationPageAppProps> = ({
  isGuruAIEligible,
}) => {
  const { session } = useAuth()
  const { sendMessage, containerRef } = useConversation()
  const { hasConversationId, savedMessages, conversationId } =
    useConversationStore()
  const {
    errorBoundary,
    isConnecting,
    isStreaming,
    isConfirmSheetOpen,
    messages,
    hasMessages,
    setMessages,
    resetMessages,
    setConfirmSheetOpen,
  } = useConversationMessageStore()
  const { SETrackConversationPage } = useSETrackEvent()

  React.useEffect(() => {
    SETrackConversationPage('GURU_AI_PAGE', {
      event_trigger: 'HOMEPAGE',
      is_new_ai_chat_session: [!hasConversationId(), 'bool'],
      ai_chat_session_id: conversationId || null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // prefill the displayed "messages" from the "savedMessages" store
  React.useEffect(() => {
    if (
      session?.user?.id &&
      Object.keys(savedMessages).length > 0 &&
      Object.keys(messages).length === 0
    ) {
      setMessages(savedMessages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, savedMessages])

  // set store name unique to the user and rehydrate the store
  React.useEffect(() => {
    if (session?.user?.id) {
      // rename the store
      useConversationStore?.persist.setOptions({
        name: `${CONVERSATION_STORAGE_KEY}-${session.user.id}` as string,
      })
      // rehydrate the store
      useConversationStore?.persist.rehydrate()
      // remove the default index
      localStorage.removeItem(CONVERSATION_STORAGE_KEY)
    }
  }, [session?.user?.id])

  // reset displayed "messages" when unmount
  React.useEffect(() => {
    return () => {
      resetMessages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isGuruAIEligible) {
    throw new ForbiddenError(null, new Error('User not in experiment'))
  }

  if (errorBoundary) {
    throw errorBoundary
  }

  return (
    <>
      <ConversationHeader />

      <main className="relative min-h-screen overflow-x-hidden">
        <section
          ref={containerRef}
          id="conversation-body"
          aria-label="Chatbot conversation"
          className="absolute inset-0 w-full h-full pt-16 overflow-x-hidden overflow-y-auto pb-36"
          aria-live="polite"
          tabIndex={0}
        >
          {hasConversationId() && hasMessages() ? (
            <>
              {Object.values(messages).map((message, index) => {
                if (message?.status === 'error') {
                  return (
                    <ConversationMessageError
                      key={`conversation-message-error-${message.messageId}`}
                      {...message}
                      isLoading={message.type === 'server' && isConnecting}
                      isLastItem={index === Object.entries(messages).length - 1}
                    />
                  )
                }

                return (
                  <ConversationMessage
                    key={`conversation-message-${message.messageId}`}
                    mode={message.type}
                    messageTokens={message?.tokens ?? []}
                    messageId={message?.messageId ?? null}
                    index={index}
                    isLastItem={index === Object.entries(messages).length - 1}
                  />
                )
              })}

              {isConnecting && !isStreaming && <ConversationMessageLoading />}
            </>
          ) : (
            <ConversationSuggestions
              onClick={(text, index) => sendMessage(text, index, true)}
            />
          )}
        </section>

        <ConversationInputBox />

        <NewSessionConfirmSheet
          isOpen={isConfirmSheetOpen}
          onClose={() => {
            setConfirmSheetOpen(false)
          }}
        />
      </main>
    </>
  )
}

export default ConversationPageApp
